import type { ActionInput } from "../simulation";

type InputAction = "left" | "right" | "jump" | "drop" | "fire";

const KEY_ACTIONS: Readonly<Record<string, InputAction>> = {
  ArrowLeft: "left",
  KeyA: "left",
  ArrowRight: "right",
  KeyD: "right",
  ArrowUp: "jump",
  KeyW: "jump",
  Space: "jump",
  ArrowDown: "drop",
  KeyS: "drop",
  KeyF: "fire",
  KeyX: "fire",
  Enter: "fire"
};

interface InputSnapshot {
  left: boolean;
  right: boolean;
  jump: boolean;
  drop: boolean;
  fire: boolean;
}

function emptySnapshot(): InputSnapshot {
  return { left: false, right: false, jump: false, drop: false, fire: false };
}

export class InputController {
  private keyboard = emptySnapshot();
  private touchPointers = new Map<number, InputAction>();
  private touchCounts: Record<InputAction, number> = { left: 0, right: 0, jump: 0, drop: 0, fire: 0 };
  private touchRoot: HTMLElement | null = null;
  private enabled = false;
  private disposed = false;

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (!this.enabled) return;
    const action = KEY_ACTIONS[event.code];
    if (!action) return;
    event.preventDefault();
    this.keyboard[action] = true;
  };

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    const action = KEY_ACTIONS[event.code];
    if (!action) return;
    event.preventDefault();
    this.keyboard[action] = false;
  };

  private readonly handleBlur = (): void => {
    this.keyboard = emptySnapshot();
    this.releaseAllPointers();
  };

  constructor() {
    window.addEventListener("keydown", this.handleKeyDown, { passive: false });
    window.addEventListener("keyup", this.handleKeyUp, { passive: false });
    window.addEventListener("blur", this.handleBlur);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.handleBlur();
    }
  }

  attachTouchControls(root: HTMLElement): void {
    this.detachTouchControls();
    this.touchRoot = root;
    root.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((button) => {
      button.addEventListener("pointerdown", this.handlePointerDown);
      button.addEventListener("pointerup", this.handlePointerEnd);
      button.addEventListener("pointercancel", this.handlePointerEnd);
      button.addEventListener("lostpointercapture", this.handlePointerEnd);
      button.addEventListener("contextmenu", this.preventContextMenu);
    });
  }

  read(): ActionInput {
    const pad = this.readGamepad();
    const left = this.keyboard.left || this.touchCounts.left > 0 || pad.left;
    const right = this.keyboard.right || this.touchCounts.right > 0 || pad.right;
    const move: -1 | 0 | 1 = left === right ? 0 : left ? -1 : 1;
    return {
      move,
      jump: this.keyboard.jump || this.touchCounts.jump > 0 || pad.jump,
      drop: this.keyboard.drop || this.touchCounts.drop > 0 || pad.drop,
      fire: this.keyboard.fire || this.touchCounts.fire > 0 || pad.fire
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.detachTouchControls();
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
    window.removeEventListener("blur", this.handleBlur);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.hidden) this.handleBlur();
  };

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (!this.enabled) return;
    const button = event.currentTarget as HTMLButtonElement;
    const action = button.dataset.action as InputAction | undefined;
    if (!action || this.touchPointers.has(event.pointerId)) return;
    event.preventDefault();
    button.setPointerCapture(event.pointerId);
    this.touchPointers.set(event.pointerId, action);
    this.touchCounts[action] += 1;
    button.setAttribute("aria-pressed", "true");
  };

  private readonly handlePointerEnd = (event: PointerEvent): void => {
    const action = this.touchPointers.get(event.pointerId);
    if (!action) return;
    event.preventDefault();
    this.touchPointers.delete(event.pointerId);
    this.touchCounts[action] = Math.max(0, this.touchCounts[action] - 1);
    const button = event.currentTarget as HTMLButtonElement;
    if (this.touchCounts[action] === 0) {
      button.setAttribute("aria-pressed", "false");
    }
  };

  private readonly preventContextMenu = (event: Event): void => {
    event.preventDefault();
  };

  private detachTouchControls(): void {
    this.touchRoot?.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((button) => {
      button.removeEventListener("pointerdown", this.handlePointerDown);
      button.removeEventListener("pointerup", this.handlePointerEnd);
      button.removeEventListener("pointercancel", this.handlePointerEnd);
      button.removeEventListener("lostpointercapture", this.handlePointerEnd);
      button.removeEventListener("contextmenu", this.preventContextMenu);
    });
    this.touchRoot = null;
    this.releaseAllPointers();
  }

  private releaseAllPointers(): void {
    this.touchPointers.clear();
    this.touchCounts = { left: 0, right: 0, jump: 0, drop: 0, fire: 0 };
    this.touchRoot?.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((button) => {
      button.setAttribute("aria-pressed", "false");
    });
  }

  private readGamepad(): InputSnapshot {
    const result = emptySnapshot();
    if (!this.enabled || typeof navigator.getGamepads !== "function") return result;
    const gamepad = Array.from(navigator.getGamepads()).find((candidate) => candidate?.connected);
    if (!gamepad) return result;
    const horizontal = gamepad.axes[0] ?? 0;
    const vertical = gamepad.axes[1] ?? 0;
    result.left = horizontal < -0.35 || Boolean(gamepad.buttons[14]?.pressed);
    result.right = horizontal > 0.35 || Boolean(gamepad.buttons[15]?.pressed);
    result.drop = vertical > 0.5 || Boolean(gamepad.buttons[13]?.pressed);
    result.jump = Boolean(gamepad.buttons[0]?.pressed || gamepad.buttons[12]?.pressed);
    result.fire = Boolean(gamepad.buttons[1]?.pressed || gamepad.buttons[2]?.pressed);
    return result;
  }
}

